'use client';

import React from 'react';
import { Document } from '@contentful/rich-text-types';
import Script from 'next/script';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import { BASE_URL } from '@src/typescriptGlobals/constants';
import Dot from '@src/components/Icons/Dot';
import Container from '@src/components/Shared/Container';
import NextImage from '@src/components/Shared/NextImage';
import ContentfulRenderer from '@src/components/Shared/ContentfulRenderer';
import isEmpty from '@src/utils/isEmpty';
import dateFormatter from '@src/utils/dateFormatter';
import useInspectorMode from '@src/hooks/useInspectorMode';
import constructURL from '@src/utils/constructURL';
import usePreviewContent from '@src/hooks/usePreviewContent';
import LanguageSwitcher from '@src/components/Shared/LanguageSwitcher';
import { PostPageClientPropTypes } from './PostPage.types';
const PostPageClient: React.FC<PostPageClientPropTypes> = ({
  data,
  locale
}) => {
  const [geo, setGeo] = React.useState({
    country: 'US',
    locale: 'en'
  });

  // Enable live updates for the post
  const post = usePreviewContent({
    content: data?.post
  });

  // Enable inspector mode for the entry
  const inspectorProps = useInspectorMode({
    entryId: post?.sys?.id
  });

  // Set the page view path for analytics tracking
  const {
    setPathPageView,
    setContentType
  } = useTrackingCode();
  setPathPageView(constructURL({
    languageCode: data?.locale,
    slug: `/news/${data?.slug}`,
    baseURL: ''
  }));
  setContentType('site-page');
  React.useEffect(() => {
    async function fetchGeo() {
      const res = await fetch(`/api/geo`);
      const {
        country,
        locale
      } = await res.json();
      setGeo({
        country,
        locale
      });
    }
    fetchGeo();
  }, []);
  React.useEffect(() => {
    if (geo?.country === 'CA' || locale === 'en-ca') {
      const cano = document.querySelector('link[rel="canonical"]');
      const xDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
      const enHflang = document.querySelector('link[rel="alternate"][hreflang="en"]');
      if (cano && locale === 'en-ca') {
        const href = cano.getAttribute('href') || '';
        const url = new URL(href, BASE_URL);
        // only prepend if it's not already there
        const newPath = url.pathname.startsWith(`/${locale}`) ? url.pathname : `/${locale}${url.pathname}`;
        cano.setAttribute('href', BASE_URL + newPath);
      }
      if (xDefault) {
        const href = xDefault.getAttribute('href') || '';
        const url = new URL(href, BASE_URL);
        // only prepend if it's not already there
        const newPath = url.pathname.startsWith(`/en-ca`) ? url.pathname : `/en-ca${url.pathname}`;
        xDefault.setAttribute('href', BASE_URL + newPath);
      }
      if (enHflang) {
        const href = enHflang.getAttribute('href') || '';
        const url = new URL(href, BASE_URL);
        // only prepend if it's not already there
        const newPath = url.pathname.startsWith(`/${geo?.country === 'CA' ? 'en-ca' : locale}`) ? url.pathname : `/${geo?.country === 'CA' ? 'en-ca' : locale}${url.pathname}`;
        enHflang.setAttribute('href', BASE_URL + newPath);
        enHflang.setAttribute('hreflang', geo?.country === 'CA' ? 'en-ca' : locale);
      }
    }
  }, [geo]);
  return <>
      <Container className='mt-[5rem]' data-sentry-element="Container" data-sentry-source-file="PostPage.client.tsx">
        <div className='mx-auto max-w-[80rem]'>
          {post?.title && <h1 className='mb-[2.4rem] text-[2rem] font-[500] leading-[2.4rem] text-body-copy--dark  md:text-[3.2rem] md:leading-[4rem] 3xl:text-[4.8rem] 3xl:leading-[5.6rem]' {...inspectorProps({
          fieldId: 'title'
        })}>
              {post?.title}
            </h1>}

          <div className='mb-[1.6rem] flex flex-wrap items-center gap-[0.8rem] text-[1.4rem] md:text-[1.6rem]' {...inspectorProps({
          fieldId: 'categories'
        })}>
            <span className='font-[400] text-body-copy--dark'>
              {post?.categoriesCollection?.items?.[0]?.title ?? ''}
            </span>
            {!isEmpty({
            value: post?.datePublished
          }) ? <>
                <Dot className='text-body-copy--dark' />
                <time dateTime={post?.datePublished}>{dateFormatter(post?.datePublished)}</time>
              </> : ''}
            {new Date(post?.sys?.publishedAt) > new Date(post?.datePublished) && <span className='capitalize sm:ml-auto'>
                {data?.globalTextBlock?.lastUpdated}{' '}
                <time dateTime={post?.sys?.publishedAt}>
                  {dateFormatter(post?.sys?.publishedAt)}
                </time>
              </span>}
          </div>

          <div className='mb-[3rem] flex items-center' {...inspectorProps({
          fieldId: 'languageCode'
        })}>
            <p>{data?.globalTextBlock?.availableIn}</p>
            <LanguageSwitcher post={post} data-sentry-element="LanguageSwitcher" data-sentry-source-file="PostPage.client.tsx" />
          </div>

          <NextImage className='mb-[2.4rem] bg-background-primary' priority entryId={post?.sys?.id} fieldId='featuredImage' src={post?.featuredImage?.url} alt={post?.featuredImage?.description ?? ''} width={post?.featuredImage?.width} aspectRatio={post?.featuredImage?.width / post?.featuredImage?.height} data-sentry-element="NextImage" data-sentry-source-file="PostPage.client.tsx" />

          <ContentfulRenderer document={post?.body?.json as Document} links={post?.body?.links} inspectorProps={inspectorProps({
          fieldId: 'body'
        })} data-sentry-element="ContentfulRenderer" data-sentry-source-file="PostPage.client.tsx" />
        </div>
      </Container>

      <Script id='app-ld-json' type='application/ld+json' dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [{
          '@type': 'Article',
          '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}/#article`,
          isPartOf: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}`
          },
          author: {
            name: 'VALD',
            '@id': 'https://valdhealth.com/#/schema/person/0919df55a24485de001dc9a298301905'
          },
          headline: `${post.title}`,
          datePublished: `${post.datePublished}`,
          dateModified: `${post?.sys?.publishedAt ?? post?.datePublished}`,
          mainEntityOfPage: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}`
          },
          wordCount: 1394,
          publisher: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#organization`
          },
          image: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}/#primaryimage`
          },
          thumbnailUrl: post.thumbnail?.url,
          articleSection: ['News &amp; Announcements'],
          inLanguage: 'en-US'
        }, {
          '@type': 'WebPage',
          '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}`,
          url: `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}`,
          name: `${post.title} | VALD Health | Human Measurement Technologies`,
          isPartOf: {
            '@id': 'https://valdhealth.com/#website'
          },
          primaryImageOfPage: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}/#primaryimage`
          },
          image: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}/#primaryimage`
          },
          thumbnailUrl: `${post.thumbnail?.url}`,
          datePublished: `${post.datePublished}`,
          dateModified: `${post?.sys?.publishedAt ?? post?.datePublished}`,
          description: `${post.excerpt}`,
          inLanguage: 'en-US',
          potentialAction: [{
            '@type': 'ReadAction',
            target: [`https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}`]
          }]
        }, {
          '@type': 'ImageObject',
          inLanguage: 'en-US',
          '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}/#primaryimage`,
          url: `${post.thumbnail?.url}`,
          contentUrl: `${post.thumbnail?.url}`,
          width: 1080,
          height: 877
        }, {
          '@type': 'BreadcrumbList',
          '@id': `"https://valdhealth.com/${post?.languageCode?.toLowerCase()}/${post.slug}/#breadcrumb`,
          itemListElement: [{
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `https://valdhealth.com/${post?.languageCode?.toLowerCase()}`
          }, {
            '@type': 'ListItem',
            position: 2,
            name: `${post.title}`
          }]
        }, {
          '@type': 'WebSite',
          '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#website`,
          url: `https://valdhealth.com/${post?.languageCode?.toLowerCase()}`,
          name: 'VALD Health | Human Measurement Technologies',
          description: 'Move. Measure. Modify',
          publisher: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#organization`
          },
          inLanguage: 'en-US'
        }, {
          '@type': 'Organization',
          '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#organization`,
          name: 'VALD Health',
          url: `https://valdhealth.com/${post?.languageCode?.toLowerCase()}`,
          logo: {
            '@type': 'ImageObject',
            inLanguage: 'en-US',
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#/schema/logo/image/`,
            url: '',
            contentUrl: '',
            caption: 'VALD Health'
          },
          image: {
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#/schema/logo/image/`
          }
        }, {
          '@type': 'Person',
          '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#/schema/person/0919df55a24485de001dc9a298301905`,
          name: 'VALD',
          image: {
            '@type': 'ImageObject',
            inLanguage: 'en-US',
            '@id': `https://valdhealth.com/${post?.languageCode?.toLowerCase()}/#/schema/person/image/`,
            url: 'https://secure.gravatar.com/avatar/b4a8fd31a201b74646a54fcd7a0e0d24?s=96&d=mm&r=g',
            contentUrl: 'https://secure.gravatar.com/avatar/b4a8fd31a201b74646a54fcd7a0e0d24?s=96&d=mm&r=g',
            caption: 'VALD'
          },
          sameAs: ['https://twitter.com/valdoffice']
        }]
      }, null, '\t')
    }} data-sentry-element="Script" data-sentry-source-file="PostPage.client.tsx" />
    </>;
};
export default PostPageClient;

//https://deploy-preview-1786--vald-health-dev.netlify.app/ja/news/force-plates-101-what-are-they-and-why-are-they-used-6oymywtyvkptruud9snl1b